// src/hooks/useBasicData.js
"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import { fetchBmiHistoryByUserId, addBmiEntry } from "../../Profile/redux/thunks/bmiHistoryThunks";
import { fetchMedicalInfoByUserId } from "../../Profile/redux/thunks/medicalThunks";

import normalizeNumber from "../functions/profile/normalizeNumber";
import calcBmi from "../functions/profile/calcBmi";
import buildBmiChart from "../functions/profile/buildBmiChart";

/**
 * useBasicData
 * --------------------------------------------------
 * Source de vérité :
 *  - height / weight / BMI → bmi_history
 *  - medical_information = snapshot (blood type, etc.)
 *
 * ✅ NEW: support { enabled }
 */
export function useBasicData(options = {}, patientData) {
  const { enabled = true } = options;

  const dispatch = useDispatch();
  const userId = patientData?.id;

  /* ==================== STATE REDUX ==================== */
  const bmiHistoryState = useSelector((s) => s.bmiHistory);
  const bmiHistoryArr = bmiHistoryState?.data ?? bmiHistoryState?.items ?? [];

  /* ==================== FETCH BMI ==================== */
  useEffect(() => {
    if (!enabled) return;
    if (!userId || bmiHistoryState?.hasFetched) return;
    dispatch(fetchBmiHistoryByUserId(userId));
  }, [dispatch, userId, bmiHistoryState?.hasFetched, enabled]);

  /* ==================== DERIVED DATA ==================== */
  const latestBmi = useMemo(() => {
    if (!enabled) return null;
    if (!Array.isArray(bmiHistoryArr) || bmiHistoryArr.length === 0) return null;
    return bmiHistoryArr[bmiHistoryArr.length - 1];
  }, [bmiHistoryArr, enabled]);

  const height = enabled ? latestBmi?.tailleCm ?? null : null;
  const weight = enabled ? latestBmi?.poidsKg ?? null : null;

  const { bmi, status: bmiStatus } = useMemo(() => {
    if (!enabled) return { bmi: null, status: null };
    return calcBmi(height, weight);
  }, [height, weight, enabled]);

  /* ==================== LOCAL UI STATE ==================== */
  const [isEditing, setIsEditing] = useState(false);
  const [tempHeight, setTempHeight] = useState("");
  const [tempWeight, setTempWeight] = useState("");
  const [saving, setSaving] = useState(false);

  /* ==================== ACTIONS ==================== */
  const handleEditClick = useCallback(() => {
    if (!enabled) return;
    setTempHeight(height ?? "");
    setTempWeight(weight ?? "");
    setIsEditing(true);
  }, [height, weight, enabled]);

  const handleCancelClick = useCallback(() => {
    setIsEditing(false);
    setTempHeight("");
    setTempWeight("");
  }, []);

  const handleSaveClick = useCallback(
    async (newHeight, newWeight) => {
      if (!enabled) return;
      if (!userId) return;

      const heightToSave =
        newHeight !== undefined ? normalizeNumber(newHeight) : normalizeNumber(tempHeight);

      const weightToSave =
        newWeight !== undefined ? normalizeNumber(newWeight) : normalizeNumber(tempWeight);

      if (!heightToSave || !weightToSave) {
        message.warning("Veuillez saisir une taille et un poids valides.");
        return;
      }

      try {
        setSaving(true);

        await dispatch(
          addBmiEntry({
            userId,
            values: {
              tailleCm: Number(heightToSave),
              poidsKg: Number(weightToSave),
            },
          })
        ).unwrap();

        await dispatch(fetchBmiHistoryByUserId(userId));
        await dispatch(fetchMedicalInfoByUserId(userId));

        setIsEditing(false);
        message.success("✅ Informations mises à jour !");
      } catch (err) {
        message.error("❌ Erreur lors de la mise à jour.");
      } finally {
        setSaving(false);
      }
    },
    [dispatch, userId, tempHeight, tempWeight, enabled]
  );

  /* ==================== CHART ==================== */
  const chartData = useMemo(() => {
    if (!enabled) return [];
    const result = buildBmiChart(bmiHistoryArr);
    return result;
  }, [bmiHistoryArr, enabled]);

  /* ==================== API ==================== */
  return {
    height,
    weight,
    bmi,
    bmiStatus,

    isEditing: enabled ? isEditing : false,
    tempHeight,
    tempWeight,
    setTempHeight,
    setTempWeight,

    handleEditClick,
    handleCancelClick,
    handleSaveClick,

    saving: enabled ? saving : false,
    chartData,
  };
}

export default useBasicData;
